import { defineContentTabs } from './contentTabs'
import { defineCurrentTime } from './currentTime'
import { defineMessageCallout } from './messageCallout'
import { defineProgressMeter } from './progressMeter'

const PROFILE_TAG = 'user-profile'
const ACCORDION_TAG = 'ui-accordion'
const ACCORDION_SECTION_TAG = 'accordion-section'

export function defineRichElements(): void {
  if (typeof window === 'undefined' || !window.customElements) return

  if (!window.customElements.get(PROFILE_TAG)) {
    class UserProfileElement extends HTMLElement {
      connectedCallback() {
        const userId = (this.getAttribute('user-id') ?? '').slice(0, 80)
        const badge = document.createElement('span')
        badge.setAttribute('part', 'badge')
        badge.textContent = `User #${userId || 'unknown'}`
        badge.style.display = 'inline-flex'
        badge.style.alignItems = 'center'
        badge.style.border = '1px solid var(--rich-profile-border, #b8c5d1)'
        badge.style.borderRadius = '999px'
        badge.style.background = 'var(--rich-profile-bg, #f4f7fa)'
        badge.style.color = 'var(--rich-profile-color, #314253)'
        badge.style.padding = '2px 8px'
        badge.style.fontSize = '0.875em'
        this.replaceChildren(badge)
      }
    }

    window.customElements.define(PROFILE_TAG, UserProfileElement)
  }

  if (!window.customElements.get(ACCORDION_SECTION_TAG)) {
    class AccordionSectionElement extends HTMLElement {
      static observedAttributes = ['open', 'title']

      constructor() {
        super()
        this.attachShadow({ mode: 'open' })
      }

      connectedCallback() {
        const root = this.shadowRoot
        if (!root) return
        if (!root.firstChild) {
          root.innerHTML = `
            <style>
              :host { display: block; }
              .item { border-bottom: 1px solid var(--rich-accordion-divider, #e2e8f0); }
              .header {
                box-sizing: border-box; width: 100%; border: 0; padding: 14px 20px;
                background: var(--rich-accordion-header-bg, #f8fafc); color: var(--rich-accordion-color, #1f2933);
                cursor: pointer; font: inherit; font-weight: 600; text-align: left;
                display: flex; align-items: center; justify-content: space-between; gap: 12px;
              }
              .header:focus-visible { outline: 2px solid var(--rich-accordion-focus, #3472a9); outline-offset: -2px; }
              .body { padding: 16px 20px; background: var(--rich-accordion-body-bg, #fff); }
              .icon { transition: transform 0.2s ease; }
            </style>
            <section class="item">
              <button class="header" type="button" aria-expanded="false">
                <span class="title"></span><span class="icon" aria-hidden="true">&#9660;</span>
              </button>
              <div class="body"><slot></slot></div>
            </section>`
          root.querySelector('.header')?.addEventListener('click', () => {
            this.toggleAttribute('open')
            this.dispatchEvent(new Event('input', { bubbles: true, composed: true }))
          })
          root.querySelector('.header')?.addEventListener('dblclick', event => {
            if (!this.closest('[contenteditable="true"]')) return
            event.preventDefault()
            const title = window.prompt('Section title:', this.getAttribute('title') ?? '')?.trim()
            if (!title) return
            this.setAttribute('title', title.slice(0, 160))
            this.dispatchEvent(new Event('input', { bubbles: true, composed: true }))
          })
        }
        this.syncView()
      }

      attributeChangedCallback() {
        this.syncView()
      }

      private syncView() {
        const root = this.shadowRoot
        if (!root) return
        const isOpen = this.hasAttribute('open')
        const title = root.querySelector<HTMLElement>('.title')
        const header = root.querySelector<HTMLElement>('.header')
        const body = root.querySelector<HTMLElement>('.body')
        const icon = root.querySelector<HTMLElement>('.icon')
        if (title) title.textContent = this.getAttribute('title') || 'Section'
        header?.setAttribute('aria-expanded', String(isOpen))
        if (body) body.hidden = !isOpen
        if (icon) icon.style.transform = `rotate(${isOpen ? '180deg' : '0deg'})`
      }
    }

    window.customElements.define(ACCORDION_SECTION_TAG, AccordionSectionElement)
  }

  if (!window.customElements.get(ACCORDION_TAG)) {
    class UiAccordionElement extends HTMLElement {
      constructor() {
        super()
        this.attachShadow({ mode: 'open' })
      }

      connectedCallback() {
        const root = this.shadowRoot
        if (!root || root.firstChild) return
        root.innerHTML = `
          <style>
            :host { display: block; margin: 12px 0; }
            .wrapper {
              overflow: hidden; border: 1px solid var(--rich-accordion-border, #cbd5e1);
              border-radius: var(--rich-accordion-radius, 8px);
              box-shadow: var(--rich-accordion-shadow, 0 4px 6px -1px rgba(0, 0, 0, 0.05));
            }
            ::slotted(accordion-section:last-child) { --rich-accordion-divider: transparent; }
          </style>
          <div class="wrapper"><slot></slot></div>`
      }
    }

    window.customElements.define(ACCORDION_TAG, UiAccordionElement)
  }

  defineMessageCallout()
  defineProgressMeter()
  defineContentTabs()
  defineCurrentTime()
}
